import type { KarmaCircle, KarmaCircleRule, KarmaCircleRitual, KarmaNumerology } from "@/lib/karma/types";

function ritualId(seed: string) {
  return `auto_${seed}`;
}

function uniqueById(items: KarmaCircleRitual[]) {
  const seen = new Set<string>();
  const out: KarmaCircleRitual[] = [];
  for (const it of items) {
    if (seen.has(it.id)) continue;
    seen.add(it.id);
    out.push(it);
  }
  return out;
}

function ritualFromDigit(args: { kind: string; digit: number; nowIso: string }): KarmaCircleRitual {
  const { kind, digit, nowIso } = args;

  const base = (title: string, description: string): KarmaCircleRitual => ({
    id: ritualId(`${kind}_${digit}`),
    title,
    description,
    createdByUserId: "system",
    createdAt: nowIso,
  });

  switch (digit) {
    case 1:
      return base(
        "1 • Net karar ritüeli",
        "Bugün tek bir karar yaz: ‘Seçtim’. Ardından 60 saniye içinde ilk adımı başlat.",
      );
    case 2:
      return base(
        "2 • Bağ kurma ritüeli",
        "Bir kişiye tek cümlelik hal-hatır/teşekkür mesajı gönder. Kanıt: mesajın metnini kısaca yaz.",
      );
    case 3:
      return base(
        "3 • İfade ritüeli",
        "30 saniye: İçinden geçen tek cümleyi yaz. 30 saniye: bunu yumuşak bir dille yeniden yaz.",
      );
    case 4:
      return base(
        "4 • Düzen ritüeli",
        "2 dakika: Masada/telefonunda tek bir ‘dağınıklık’ parçasını düzelt. Kanıt: neyi düzelttiğini yaz.",
      );
    case 5:
      return base(
        "5 • Esneklik ritüeli",
        "Bugün bir şeyi ‘farklı yoldan’ yap: farklı rota, farklı sırayla iş, farklı müzik. Kanıt: neyi değiştirdin?",
      );
    case 6:
      return base(
        "6 • Şefkat ritüeli",
        "2 dakika: Kendine veya birine şefkat cümlesi yaz. ‘Şu an elimden gelen bu’ gibi.",
      );
    case 7:
      return base(
        "7 • İçgörü ritüeli",
        "60 saniye: Bugün seni zorlayan bir şeyi yaz ve altına ‘Bu bana ne öğretiyor?’ diye tek cevap ekle.",
      );
    case 8:
      return base(
        "8 • Sınır ritüeli",
        "Bugün bir sınır koy: ‘Şu an buna evet diyemem’. Kanıt: hangi konuda sınır koydun?",
      );
    case 9:
    default:
      return base(
        "9 • Tamamlama ritüeli",
        "2 dakika: Yarım kalmış tek bir şeyi tamamla ya da kapat. Kanıt: neyi kapattın?",
      );
  }
}

function toDigit(n: number) {
  const v = Math.trunc(Number(n));
  if (!Number.isFinite(v)) return null;
  if (v < 1 || v > 9) return null;
  return v;
}

function ruleToDigit(rule: KarmaCircleRule, numerology: KarmaNumerology): number | null {
  const val = Math.trunc(Number(rule.value));
  if (!Number.isFinite(val)) return null;

  if (rule.key === "karmicDebt") {
    return numerology.karmicCodes.includes(val) ? val : null;
  }

  const n =
    rule.key === "destinyNumber"
      ? numerology.destinyNumber
      : rule.key === "expressionNumber"
        ? numerology.expressionNumber
        : rule.key === "heartDesireNumber"
          ? numerology.heartDesireNumber
          : rule.key === "personalityNumber"
            ? numerology.personalityNumber
            : numerology.birthDayNumber;

  return n === val ? toDigit(val) : null;
}

export function getAutoCircleRituals(args: {
  circle: KarmaCircle;
  meNumerology: KarmaNumerology;
  now?: Date;
}) {
  const { circle, meNumerology } = args;
  const nowIso = (args.now ?? new Date()).toISOString();

  const rules = circle.rules?.all ?? [];
  if (rules.length === 0) return [];

  const rituals: KarmaCircleRitual[] = [];
  for (const r of rules) {
    const digit = ruleToDigit(r, meNumerology);
    if (!digit) continue;
    rituals.push(ritualFromDigit({ kind: r.key, digit, nowIso }));
  }

  return uniqueById(rituals).slice(0, 5);
}
